import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';

import {
  companyDataSelector,
  companyLoadSelector,
  fetchCompanyDataRequestAction,
  travelCompanyEditModeSelector,
  updateCompanyExternalMarkupPercentageAction,
} from 'store/modules/companyInfo';
import * as CompanyInfoActions from 'store/modules/companyInfo/actions';

import { ErrorBar, LoadingBar } from 'ui/NetworkStatusBar';
import { ENetworkRequestStatus } from 'services/BackendApi';
import { BackLink } from 'ui/BackLink';

import { setTravelCompanyAction } from 'store/modules/companyHotelMarkup';
import { setTravelCompanyEditModeAction } from 'store/modules/companyInfo';
import { CompanySettingsTabs } from './CompanySettingsTabs';
import { PageTitle } from 'ui/PageTitle';

import { getCurrentUserType } from 'store/modules/auth';
import { EUserType } from 'services/BackendApi';
import { Formik } from 'formik';
import { useCrmDirectoryEntry } from 'services/CrmApi/queries/useCrmDirectoryEntry';
import { CompanyProfileInfo } from 'containers/CRM/editCompany/CrmCompanyProfile/CompanyProfileInfo';
import {
  ICompanyProfileFormValues,
} from 'containers/CRM/editCompany/CrmCompanyProfile/companyProfileForm';
import { noop } from 'utils';
import { Link } from 'ui/Link';
import { TravelCompanyFormV2 } from '../TravelCompanyForm/TravelCompanyFormV2';

interface ITravelCompanySettingsRouteParams {
  companyUuid: string;
}

export const TravelCompanySettingsV2: React.FC = () => {
  const match = useRouteMatch<ITravelCompanySettingsRouteParams>();
  const companyUuid = match.params.companyUuid;
  const company = useSelector(companyDataSelector);
  const companyLoad = useSelector(companyLoadSelector);
  const userType = useSelector(getCurrentUserType) as EUserType;
  const isEditMode = useSelector(travelCompanyEditModeSelector);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setTravelCompanyEditModeAction(userType === EUserType.ADMIN));
    dispatch(setTravelCompanyAction(companyUuid));
    dispatch(fetchCompanyDataRequestAction(companyUuid));

    // We clear the company logo info when we change the company/load this component.
    // This is because, from what I can make out, the _tenant_ company logo is *initially* stored
    // in the same redux domain as the company we're editing. It's not very clear, but I believe this
    // is why if you load the Company Logo tab, you get a "flash" of the tenant company logo before
    // the actual company logo loads. This line of code fixes that.
    // TODO fully separate the tenant company info from the company we're editing
    dispatch(CompanyInfoActions.clearCompanyLogoInfo());
    return () => {
      dispatch(updateCompanyExternalMarkupPercentageAction(null));
      dispatch(CompanyInfoActions.updateCustomServiceChargeAction(null));
    };
  }, [companyUuid, dispatch]);

  const { crmDirectoryEntryResponse, directoryEntry } = useCrmDirectoryEntry(companyUuid);

  if (crmDirectoryEntryResponse.isFetching) {
    return (
      <div className="mt-[20px]">
        <LoadingBar />
      </div>
    );
  }

  if (!directoryEntry || !('companyType' in directoryEntry.profile)) {
    return (
      <div className="mt-[20px]">
        <ErrorBar message="Can not find such a company" />
      </div>
    );
  }

  const initialValues: ICompanyProfileFormValues = {
    companyTypeCode: directoryEntry.profile.companyType?.code ?? '',
    name: directoryEntry?.fullName ?? '',
    tcType: directoryEntry.profile?.tcType,
    industry: directoryEntry.profile?.industry ?? '',
    employeeCount: !directoryEntry.profile.employeeCount ? '' : String(directoryEntry.profile.employeeCount),
    countryCode: directoryEntry?.country?.code ?? null,
    address: directoryEntry?.address ?? '',
    mobile: directoryEntry?.mobile ?? '',
    email: directoryEntry?.email ?? '',
    acceptsEmail: directoryEntry?.acceptsEmail ?? false,
    website: directoryEntry.profile?.website ?? '',
    linkedin: directoryEntry?.linkedin ?? '',
    twitter: directoryEntry?.twitter ?? '',
    instagram: directoryEntry?.instagram ?? '',
    currencyCode: directoryEntry?.currency?.code ?? null,
  };

  return (
    <div className="company-settings-page w-1280px mx-auto">
      <BackLink type="internal" href="/travel-companies">
        Back to Partner Company Results
      </BackLink>
      <PageTitle
        primaryText="Settings"
        secondaryCondition={!!company?.name}
        secondaryText={`- ${company?.name}`}
        tertiaryText={directoryEntry?.beHumanReadableId ? `(${directoryEntry?.beHumanReadableId})` : undefined}
        tertiaryCondition={!!directoryEntry?.beHumanReadableId}
      />
      {companyLoad === ENetworkRequestStatus.PENDING && (
        <div className="mt-5">
          <LoadingBar />
        </div>
      )}
      {companyLoad === ENetworkRequestStatus.ERROR && (
        <div className="mt-5">
          <ErrorBar />
        </div>
      )}
      {company && (
        <div className="company-settings-layout mt-[20px] flex gap-[40px]">
          <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            onSubmit={noop}
          >
            <CompanyProfileInfo isDisabled>
              <Link
                to={`/crm/company/${directoryEntry.id}/edit`}
                internalClassName="font-pt-sans text-brown-100 text-[15px] font-[700] underline"
                className="flex flex-row-reverse"
                target="_blank"
              >
                Access Profile
              </Link>
            </CompanyProfileInfo>
          </Formik>
          <TravelCompanyFormV2 company={company} isEditMode={isEditMode}>
            <>
              <p className="heading mb-0 mt-[40px] font-pt-sans text-black-true font-bold text-17px leading-22px">
                Advanced Info
              </p>
              <CompanySettingsTabs companyUuid={companyUuid} isEditMode={isEditMode} />
            </>
          </TravelCompanyFormV2>
        </div>
      )}
    </div>
  );
};
